































































import Vue from 'vue';
import Component, { mixins } from 'vue-class-component';
import ParentComponentMixin from '@/common/ts/child-component/ParentComponentMixin';
import ChildComponentCollection from '@/common/ts/child-component/ChildComponentCollection';
import ResizeUpdateMixin from '@/common/ts/resizing/ResizeUpdateMixin';
import StageItem from '@/webcomponents/stage/StageItem.vue';
import ShadowDom from '@/common/ts/shadow-dom/ShadowDom';
import IntersectionDirective from '@/common/vue/intersection-directive';
import ScrollIndicator from '@/common/vue/ScrollIndicator.vue';
import TrackingPushService from '@/common/ts/tracking/TrackingPushService';
import { getOrCreateScrollSnap, ScrollSnapInstance } from '@/common/ts/scroll-snap/scroll-snap';
import i18n from '@/webcomponents/stage/i18n';
import VideoPauseHandler from '@/common/ts/pausing-videos/VideoPauseHandler';

const WordFade = () => import('../../common/vue/WordFade.vue');
const CustomButton = () => import('@/common/vue/CustomButton.vue');
const IconArrowRight = () => import('@/common/icons/arrow-right.svg');
const IconArrowExternal = () => import('@/common/icons/arrow-external.svg');
const RelaunchButton = () => import('@/common/vue/RelaunchButton.vue');

const StageProps = Vue.extend({
  props: {
    editMode: Boolean,
    headerIsLarge: Boolean,
    headerHasVideo: Boolean,
  },
});

@Component({
  components: {
    WordFade,
    CustomButton,
    IconArrowRight,
    IconArrowExternal,
    ScrollIndicator,
    RelaunchButton,
  },
  directives: {
    intersect: IntersectionDirective,
  },
  i18n,
})
export default class Stage extends mixins(StageProps, ParentComponentMixin, ResizeUpdateMixin) {
  $refs!: {
    itemPlaceholders: HTMLElement[];
    items: HTMLElement;
    mainHeadline: HTMLElement;
    subHeadline: HTMLElement;
  };

  items = new ChildComponentCollection<StageItem>();
  activeItem: StageItem | null = null;
  scrollSnap: ScrollSnapInstance | undefined;
  initial = true;
  uncovered = false;
  noGarageDoorTransition = false;
  overlayVisible = true;
  headlineVisible = false;
  hasScrollOffset = false;
  textState = 'hidden';
  headerScrollState = 'scroll-up';
  headerSpacing = 72;
  snappingThreshold = 0.2;
  moduleHeight = 0;
  labelingOverflowHeightFirstItem = 0;
  labelingOverflowHeightLastItem = 0;
  activeIndex = 0;
  section = 0;
  scrollIndicatorVisible = false;
  scrollDirection = 'forward';
  videoProgressHidden = false;
  overlayOffset = 0;
  mobileLongThreshold = 660;

  created () {
    this.headerScrollState = this.headerIsLarge ? 'large' : 'scroll-up';
    this.initParentComponentMixin(
      this.items,
      ['StageItem'],
      this.onItemAdded,
    );
    this.updateHeight();
    this.$on('resizeEnd', this.updateHeight);
    this.$on('resizeEnd', this.updateVideoPauseHandler);
  }

  mounted () {
    document.addEventListener('header-state-change', this.onHeaderStateChange, { passive: true });
    setTimeout(() => {
      this.doScrollCalculation(true);
      document.addEventListener('scroll', this.onScroll, { passive: true });
    }, 0);
  }

  onItemAdded (addedItem: Vue): void {
    if (addedItem.$refs.video) {
      VideoPauseHandler.getInstance().addNewVideo(addedItem.$refs.video as HTMLVideoElement);
    }
    if (!this.activeItem || !ShadowDom.isInDocument(this.activeItem.$el)) {
      this.clearActiveStatus();
      const stageItem = addedItem as StageItem;
      stageItem.status = 'active';
      this.activeItem = stageItem;
    }
    this.updateHeight();
  }

  updated (): void {
    if (!this.editMode && !window.location.search.includes('nosnap')) {
      if (!this.scrollSnap) {
        this.scrollSnap = getOrCreateScrollSnap(document.documentElement, {
          snapDestinationYElements: this.$refs.itemPlaceholders,
        });
      } else {
        this.scrollSnap.addSnapDestinationYElements(this.$refs.itemPlaceholders);
      }
    }
    this.updateHeight();
  }

  updateVideoPauseHandler () {
    this.items.forEach((item) => {
      if (item.$refs.video) {
        VideoPauseHandler.getInstance().addNewVideo(item.$refs.video);
      }
    });
    this.updateHeight();
  }

  updateHeight () {
    // labeling height
    const firstItem = this.items.first();
    const lastItem = this.items.last();
    this.labelingOverflowHeightFirstItem = firstItem ? firstItem.labelingOverflowHeight : 0;
    this.labelingOverflowHeightLastItem = lastItem ? lastItem.labelingOverflowHeight : 0;
    // module height
    this.moduleHeight = this.items.length * window.innerHeight;
  }

  get rootClass () {
    return {
      'brandhub-stage--initial': this.initial,
      'brandhub-stage--backward': this.scrollDirection === 'backward',
      'brandhub-stage--primary': this.activeItem?.textColor === 'primary',
      'brandhub-stage--mobile-long': this.isMobile && this.windowHeight >= this.mobileLongThreshold,
      'brandhub-stage--uncovered': this.uncovered,
      'brandhub-stage--no-garage-door-transition': this.noGarageDoorTransition,
      'brandhub-stage--has-scroll-offset': this.hasScrollOffset,
      'brandhub-stage--header-is-large-and-expanded': this.headerIsLarge && this.headerScrollState === 'large',
      'brandhub-stage--edit-mode': this.editMode,
    };
  }

  get rootStyle () {
    return {
      '--module-height': `${this.moduleHeight}px`,
      '--fade-in-delay': this.headerHasVideo && this.initial ? '1500ms' : '0ms',
      '--fade-in-delay-labeling': `${this.labelingDelay}ms`,
      '--labeling-overflow-height-first-item': `${this.labelingOverflowHeightFirstItem}px`,
      '--labeling-overflow-height-last-item': `${this.labelingOverflowHeightLastItem}px`,
    };
  }

  get labelingDelay (): number {
    if (this.headerHasVideo && this.initial) {
      return 4000;
    }
    if (this.scrollDirection === 'backward') {
      return 3000;
    }
    return 2750;
  }

  get headlineDelay () {
    if (this.scrollDirection === 'backward') {
      return 0;
    }
    return 1000 + (this.initial && this.headerHasVideo ? 1500 : 0);
  }

  get headlineAlignment () {
    return (this.isMobile || this.isTabletPortrait || this.isTablet ? 'always-center' : 'left');
  }

  get overlayClass () {
    return {
      'brandhub-stage__overlay--visible': this.overlayVisible,
    };
  }

  get textClass () {
    return {
      'brandhub-stage__text--visible': this.textState === 'visible',
      'brandhub-stage__text--hidden': this.textState === 'hidden',
    };
  }

  get scrollIndicatorClass () {
    return {
      'brandhub-stage__scroll-indicator--visible': this.scrollIndicatorVisible,
      'brandhub-stage__scroll-indicator--forward': this.scrollDirection === 'forward',
      'brandhub-stage__scroll-indicator--backward': this.scrollDirection === 'backward',
      'brandhub-stage__scroll-indicator--progress-hidden': this.videoProgressHidden,
    };
  }

  get extraThresholdFirstItem (): number {
    if (this.isMobile || this.isTabletPortrait) {
      return Math.ceil(this.labelingOverflowHeightFirstItem);
    }
    return 0;
  }

  get extraThresholdLastItem (): number {
    if (this.isMobile || this.isTabletPortrait) {
      return Math.ceil(this.labelingOverflowHeightLastItem) + this.headerSpacing;
    }
    return 0;
  }

  headlineMounted () {
    this.overlayOffset = (this.$refs.mainHeadline.offsetHeight + this.$refs.subHeadline.offsetHeight) / 2;
  }

  onScroll () {
    this.doScrollCalculation(false);
  }

  doScrollCalculation (initScrollPositionOnLoad: boolean) {
    const newSection = this.calcSection();
    // const maxScroll = 82 + (this.items.length - 1) * (window.innerHeight * this.snappingThreshold);
    const maxScroll = this.moduleHeight + this.labelingOverflowHeightLastItem;
    if (!initScrollPositionOnLoad
      && (!this.activeItem || window.scrollY > maxScroll) && newSection === this.section) {
      // skip calculation when outside of area
      return;
    }
    const offset = this.headerSpacing + this.activeIndex * (window.innerHeight * this.snappingThreshold);
    if (offset > window.scrollY) {
      this.$refs.items.style.removeProperty('--items-scroll-offset');
      this.hasScrollOffset = false;
    }

    // set initial section (when already scrolled down and reloading page in browser)
    if (initScrollPositionOnLoad && newSection > 0) {
      this.setActiveIndex(newSection);
      this.section = newSection;
      this.initial = false;
      this.noGarageDoorTransition = true;
      this.uncovered = true;
    }

    // handle section
    if (newSection === this.section) {
      // handle scrolling within current section
      if (this.activeIndex === 0) {
        this.$refs.items.style.setProperty('--items-scroll-offset', `translateY(-${window.scrollY - offset}px)`);
        this.hasScrollOffset = (window.scrollY - offset) >= 30;
      } else {
        this.$refs.items.style.removeProperty('--items-scroll-offset');
        this.hasScrollOffset = false;
      }
    } else if (newSection > this.section) {
      // go to next section
      this.goForward();
    } else {
      // go back to prev section
      this.goBackward();
    }

    this.section = newSection;
  }

  onHeaderStateChange (event: Event) {
    this.headerScrollState = (event as CustomEvent).detail.scrollState;
  }

  goForward () {
    if (this.activeItem) {
      if (this.activeIndex !== this.section) {
        // enforce correct item being active before starting new transition (might not be active
        // yet if previous transition was not completed yet)
        this.setActiveIndex(this.section);
      }
      this.initial = false;
      this.activeItem.$el.removeEventListener('transitionend', this.onForwardTransitionEnd);
      this.activeItem.$el.removeEventListener('transitionend', this.onBackwardTransitionEnd);
      this.activeItem.$el.addEventListener('transitionend', this.onForwardTransitionEnd);
      this.activeItem.status = 'active-out';
      this.overlayVisible = false;
      this.scrollDirection = 'forward';
      this.videoProgressHidden = true;
      setTimeout(() => {
        this.headlineVisible = false;
        this.textState = 'hidden';
      }, 250);
    }
  }

  goBackward () {
    if (this.activeItem) {
      if (this.activeIndex !== this.section) {
        // enforce correct item being active before starting new transition (might not be active
        // yet if previous transition was not completed yet)
        this.setActiveIndex(this.section);
      }
      this.activeItem.$el.removeEventListener('transitionend', this.onForwardTransitionEnd);
      this.activeItem.$el.removeEventListener('transitionend', this.onBackwardTransitionEnd);
      this.activeItem.$el.addEventListener('transitionend', this.onBackwardTransitionEnd);
      this.activeItem.status = 'active-back-out';
      this.items.items[this.activeIndex - 1].status = 'prev-back-in';
      this.noGarageDoorTransition = true;
      this.overlayVisible = false;
      this.scrollDirection = 'backward';
      this.videoProgressHidden = true;
      setTimeout(() => {
        this.headlineVisible = false;
        this.textState = 'hidden';
      }, 250);
    }
  }

  onForwardTransitionEnd (evt: Event) {
    if ((evt as TransitionEvent).propertyName === 'transform' && evt.target) {
      evt.target.removeEventListener('transitionend', this.onForwardTransitionEnd);
      this.noGarageDoorTransition = true;
      this.uncovered = false;
      this.pauseVideo();
      this.setActiveIndex(1);
      this.$refs.items.style.removeProperty('--items-scroll-offset');
      this.hasScrollOffset = false;
      setTimeout(() => {
        this.noGarageDoorTransition = false;
        this.uncovered = true;
        this.overlayVisible = true;
        this.headlineVisible = true;
        this.textState = 'visible';
        this.playVideo();
      }, 250);
      setTimeout(() => {
        this.videoProgressHidden = false;
      }, 1000);
    }
  }

  onBackwardTransitionEnd (evt: Event) {
    if ((evt as TransitionEvent).propertyName === 'transform' && evt.target) {
      evt.target.removeEventListener('transitionend', this.onBackwardTransitionEnd);
      this.pauseVideo();
      this.setActiveIndex(0);
      this.overlayVisible = true;
      this.headlineVisible = true;
      this.textState = 'visible';
      this.playVideo();
      this.videoProgressHidden = false;
    }
  }

  calcSection () {
    // only implemented for two items
    const snapOffset = this.headerSpacing + (window.innerHeight * this.snappingThreshold)
      + this.extraThresholdFirstItem;
    if (window.scrollY > snapOffset && this.items.length >= 2) {
      return 1;
    }
    return 0;
  }

  setActiveIndex (index: number) {
    this.clearActiveStatus();
    this.items.items[index].status = 'active';
    this.activeIndex = index;
    this.activeItem = this.items.items[index];
  }

  clearActiveStatus () {
    this.items.forEach((item: StageItem) => {
      item.status = null;
    });
  }

  handleIntersection (isIntersecting: boolean) {
    if (isIntersecting) {
      this.uncovered = true;
      this.headlineVisible = true;
      this.textState = 'visible';
      this.scrollIndicatorVisible = true;
    }
  }

  pauseVideo () {
    if (this.activeItem && this.activeItem.$refs.video) {
      this.activeItem.$refs.video.pause();
    }
  }

  playVideo () {
    if (this.activeItem && this.activeItem.$refs.video) {
      this.activeItem.$refs.video.play();
    }
  }

  onItemClick () {
    if (this.activeItem) {
      if (this.activeItem.linkText) {
        const link = this.activeItem.externalLink ? this.activeItem.externalLink : this.activeItem.internalLink;
        TrackingPushService.getInstance().pushTrackingAttributes('link', 'Stage', this.activeItem.linkText, link);
      }
    }
  }
}
